import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import posthog from '../../posthog';
import { getSignupAttribution } from '../auth.api';

/**
 * Push identity and context into the third-party analytics SDKs.
 *
 * Extracted from user-state-context so that file holds auth, permissions and
 * company switching without three effects of write-only vendor plumbing
 * alongside them. This hook consumes state and pushes outward — nothing in the
 * app reads from it, which is why it is a hook rather than a context.
 *
 * Deliberately three separate effects: Pendo and PostHog have different
 * lifecycle branches (PostHog used to live inside Pendo's `else if`, so it was
 * skipped entirely once Pendo was already initialized), and Sentry depends on
 * `token`, which the other two do not.
 */
export function useAnalyticsIdentity({
  user,
  asCompany,
  userRole,
  token
}) {
  const aliasedUserIdRef = useRef(null);
  const attributionUserIdRef = useRef(null);

  /**
   * Keep Pendo in sync with current state.
   *
   * @listens user
   * @listens asCompany
   */
  useEffect(() => {
    const isPlg = asCompany?.account_type === 'PLG';
    if (user && asCompany && window.pendo.host && window.pendo?.isReady()) {
      //pendo is already initalized, it just needs an update
      window.pendo.updateOptions({
        visitor: {
          company: asCompany.id,
          companyName: asCompany.name,
          permissions: user.permissions,
          role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
          is_plg: isPlg
        },
        account: {
          id: asCompany.id,
          name: asCompany.name,
          account_type: asCompany.account_type || null,
          is_plg: isPlg
        }
      });
    } else if (user && asCompany) {
      window.pendo.initialize({
        visitor: {
          id: user.id,
          // Required if user is logged in, default creates anonymous ID
          email: user.email,
          // Recommended if using Pendo Feedback, or NPS Email
          full_name: user.first_name + ' ' + user.last_name,
          // Recommended if using Pendo Feedback
          company: asCompany.id,
          companyName: asCompany.name,
          permissions: user.permissions,
          role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
          industry: asCompany.naics_title,
          is_plg: isPlg
        },
        account: {
          id: asCompany.id,
          // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
          name: asCompany.name,
          account_type: asCompany.account_type || null,
          is_plg: isPlg
        }
      });
    }
  }, [user, asCompany]);

  /**
   * Keep PostHog in sync with current state.
   *
   * Separate from the Pendo effect above: this used to live inside its
   * `else if` branch, so once Pendo was already initialized every
   * identify, alias, group and super-property write was skipped.
   *
   * @listens user
   * @listens asCompany
   */
  useEffect(() => {
    if (!user || !asCompany) return;
    const isPlg = asCompany?.account_type === 'PLG';
    const identityId = String(user.id);

    // Employees are still identified — filter them out in PostHog dashboards
    // via `is_internal_user`. Opting out (previous behavior) stranded each
    // employee's pre-opt-out anonymous session as a permanent ghost person.
    // Reads the server-computed field so the definition lives in exactly
    // one place (User.is_cinch_employee on the backend).
    const isInternalUser = !!user.is_cinch_employee;
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
    if (user.email && aliasedUserIdRef.current !== identityId) {
      aliasedUserIdRef.current = identityId;
      posthog.alias(user.email.toLowerCase(), identityId);
    }
    posthog.identify(identityId, {
      email: user.email,
      name: user.first_name + ' ' + user.last_name,
      company_id: asCompany.id,
      company_name: asCompany.name,
      role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
      is_internal_user: isInternalUser
    });
    posthog.group('company', String(asCompany.id), {
      name: asCompany.name,
      account_type: asCompany.account_type || null,
      is_plg: isPlg
    });
    if (asCompany.account) {
      posthog.group('account', String(asCompany.account), {
        name: asCompany.account_name || null,
        account_type: asCompany.account_type || null,
        is_plg: isPlg
      });
    }
    posthog.register({
      is_internal_user: isInternalUser,
      company_type: asCompany.account_type || null
    });
    if (isPlg && attributionUserIdRef.current !== identityId) {
      attributionUserIdRef.current = identityId;
      getSignupAttribution().then(({
        data
      }) => {
        // The identity can change while this is in flight (company switch,
        // re-login). Writing a previous user's UTMs onto the current person
        // would silently mis-attribute them, so drop a stale response.
        if (attributionUserIdRef.current !== identityId) return;
        if (data && Object.keys(data).length) {
          posthog.register(data);
          posthog.setPersonProperties(data);
        }
      }).catch(e => {
        console.warn('Failed to load signup attribution for PostHog:', e);
        if (attributionUserIdRef.current === identityId) {
          attributionUserIdRef.current = null;
        }
      });
    }
  }, [user, asCompany]);

  /**
   * Keep Sentry context in sync with current state.
   *
   * Its own effect because it reads `token`, which the PostHog effect above
   * does not — folding it in there would either leave `token` out of the
   * dependencies (stale context) or re-fire identify/group/register on every
   * token change.
   *
   * @listens asCompany
   * @listens token
   */
  useEffect(() => {
    if (!asCompany) return;
    Sentry.setContext('working_as', {
      id: asCompany.id,
      name: asCompany.name
    });
    Sentry.setContext('hasToken', {
      hasToken: !!token
    });
  }, [asCompany, token]);
}